// src/app/core/shared/intervention.store.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { AnalysisStore } from './analysis.store';

export type InterventionsSnapshot = {
    country: string;
    city: string | null;
    selectedOptions: Record<string, string[]>;
    parameterValues: Record<number, number[]>;
    parameters: Record<string, any>;
    costmetrics: Record<string, Record<string, number>>;
    createdAt: number;
    activeCount: number; // derived count of checked interventions
};

@Injectable({ providedIn: 'root' })
export class InterventionStore {
    private readonly snapshotsSubject = new BehaviorSubject<Record<string, InterventionsSnapshot>>({});
    readonly snapshots$ = this.snapshotsSubject.asObservable();

    readonly activeInterventions$: Observable<InterventionsSnapshot | undefined>;
    readonly activeCount$: Observable<number>;

    constructor(private analysisStore: AnalysisStore) {
        this.activeInterventions$ = combineLatest([
            this.analysisStore.selectedRunId$,
            this.snapshots$
        ]).pipe(
            map(([runId, mapObj]) => (runId ? mapObj[runId] : undefined)),
            distinctUntilChanged()
        );

        this.activeCount$ = this.activeInterventions$.pipe(
            map(snap => (snap?.activeCount ?? 0))
        );
    }

    setSnapshot(simulationId: string, snapshot: Omit<InterventionsSnapshot, 'activeCount'>): void {
        const activeCount = this.computeActiveCount(snapshot.selectedOptions);
        const current = this.snapshotsSubject.value;
        this.snapshotsSubject.next({
            ...current,
            [simulationId]: { ...snapshot, activeCount }
        });
    }

    removeSnapshot(simulationId: string): void {
        const current = { ...this.snapshotsSubject.value };
        delete current[simulationId];
        this.snapshotsSubject.next(current);
    }

    getSnapshot(simulationId: string): InterventionsSnapshot | undefined {
        return this.snapshotsSubject.value[simulationId];
    }

    snapshot$(simulationId: string) {
        return this.snapshots$.pipe(
            map(mapObj => mapObj[simulationId]),
            distinctUntilChanged()
        );
    }

    private computeActiveCount(selectedOptions: Record<string, string[]>): number {
        return Object.values(selectedOptions || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
    }
}